import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import {
  FlowData,
  FlowIssue,
  FlowMode,
  SavingState,
  ProcessedNode,
  FlowNodeData,
  SettingParam,
} from "@/types/node";
import {debounce} from "lodash";
import {Node, useReactFlow} from "@xyflow/react";
import {LATEST_FLOW_DATA_VERSION} from "@/lib/flowMigrations";
import {FlowBuilderSettings} from "@/config/flowBuilderSettings";
import {useVariables} from "@/contexts/VariablesContext";
import {useSubflows} from "@/contexts/SubflowsContext";
import {convertToFSM} from "@/lib/fsmConverter";
import {
  remoteValidateFsmCollectIssues,
  findAllSubflowsIssues,
} from "@/lib/utils";
import {serializeFlowData, restoreNodeSettings} from "@/utils/index";
import {updateAssistantDetails, useUpdateAction} from "@/api/services/queries";
import {
  CustomAction,
  TransferCallAction,
  RealTimeBookingAction,
} from "@/api/services";
import {actionNodeTransform} from "@/api/transform/actions";
import {Assistant} from "@/api/services";
const DEBOUNCE_TIME = 700;

interface SaveContextType {
  savingState: SavingState;
  handleSave: (
    afterSave?: () => void,
    saveFSM?: boolean,
    allowOnLiveMode?: boolean,
  ) => void;
  setSavingState: (state: SavingState) => void;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (state: boolean) => void;
  setInitialFlowData: (data: string) => void;
  initialFlowData: string;
  flowIssues: FlowIssue[];
  setFlowIssues: (issues: FlowIssue[]) => void;
  flowMode: FlowMode;
  setFlowMode: (mode: FlowMode) => void;
  flowData: FlowData;
  setFlowData: (data: FlowData) => void;
  handleSaveAction: {
    (actionId: string, param: SettingParam, value: any): void;
    (actionId: string, updates: Record<string, any>): void;
  };
  blockSave: boolean;
  setBlockSave: (block: boolean) => void;
  permanentBlockSave: boolean;
  setPermanentBlockSave: (block: boolean) => void;
  processActionUpdate: (key: string, value: any) => any;
}

const SaveContext = createContext<SaveContextType | undefined>(undefined);

export const processNodes = (nodes: Node[]): ProcessedNode[] => {
  return nodes.map(node => {
    const settings = node.data?.settings;

    // Check if settings is already processed (object) or needs processing (array)
    const processedSettings = Array.isArray(settings)
      ? settings.reduce(
          (acc, setting) => {
            // Process regular params
            setting.params?.forEach(param => {
              acc[param.key] = param.value;

              // Process tabs if they exist in the param
              param.tabs?.forEach(tab => {
                tab.settings?.forEach(tabParam => {
                  acc[tabParam.key] = tabParam.value;
                });
              });
            });

            return acc;
          },
          {} as Record<string, any>,
        )
      : settings || {};

    return {
      ...node,
      data: {
        ...node.data,
        settings: processedSettings,
      },
    };
  }) as unknown as ProcessedNode[];
};

export const SaveProvider: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const [flowMode, setFlowMode] = useState<FlowMode>(FlowMode.TEST);

  const {mutateAsync: updateAction} = useUpdateAction();

  const [savingState, setSavingState] = useState<SavingState>(
    SavingState.LOADING,
  );
  const [blockSave, setBlockSave] = useState(false);
  const [permanentBlockSave, setPermanentBlockSave] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [initialFlowData, setInitialFlowData] = useState<string>("");
  const {variables, syncVariablesWithNodes} = useVariables();
  const {mainFlow, selectedSubflow, updateSubflow, subflows} = useSubflows();

  const {getNodes, getEdges} = useReactFlow();
  const [flowIssues, setFlowIssues] = useState<FlowIssue[]>([]);
  const blockSaveRef = useRef(blockSave);

  const variablesRef = useRef(variables);
  const mainFlowRef = useRef(mainFlow);
  const subflowsRef = useRef(subflows);
  const selectedSubflowRef = useRef(selectedSubflow);

  useEffect(() => {
    variablesRef.current = variables;
  }, [variables]);

  useEffect(() => {
    blockSaveRef.current = blockSave;
  }, [blockSave]);

  useEffect(() => {
    mainFlowRef.current = mainFlow;
  }, [mainFlow]);

  useEffect(() => {
    subflowsRef.current = subflows;
  }, [subflows]);

  useEffect(() => {
    selectedSubflowRef.current = selectedSubflow;
  }, [selectedSubflow]);

  const debouncedSave = useMemo(
    () =>
      debounce((afterSave?: () => void, saveFSM: boolean = false) => {
        if (blockSaveRef.current) return;

        const nodes = getNodes();
        const edges = getEdges();
        const modelID = FlowBuilderSettings.modelId;

        // Safety check: Don't save if canvas is empty
        // This prevents overwriting loaded data during initial load
        if (nodes.length === 0) {
          setSavingState(SavingState.SAVED);
          if (afterSave) afterSave();
          return;
        }

        setSavingState(SavingState.SAVING);

        // Use refs to get the latest values (important after publish/reset operations)
        const currentSelectedSubflow = selectedSubflowRef.current;
        const currentMainFlow = mainFlowRef.current;

        // Get the current flow data from the selected subflow
        const currentFlowData =
          currentSelectedSubflow?.flowData || currentMainFlow.flowData;

        // Process nodes based on current flow mode
        const processedNodes = processNodes(nodes);

        // Update the appropriate data based on current flow mode
        // Store unprocessed nodes (blueprint format) in the context
        // Nodes in context should always have settings as arrays, not objects
        if (flowMode === FlowMode.LIVE) {
          // Update live data
          const liveFlowData = {
            version: LATEST_FLOW_DATA_VERSION,
            nodes: nodes as Node<FlowNodeData>[],
            edges: edges,
          };

          // Update the subflow with new live data
          if (currentSelectedSubflow) {
            updateSubflow(currentSelectedSubflow.id, {
              flowData: {
                ...currentFlowData,
                live: liveFlowData,
              },
            });
          } else {
            updateSubflow("main", {
              flowData: {
                ...currentFlowData,
                live: liveFlowData,
              },
            });
          }
        } else {
          // Update test data
          const testFlowData = {
            version: LATEST_FLOW_DATA_VERSION,
            nodes: nodes as Node<FlowNodeData>[],
            edges: edges,
          };

          // Update the subflow with new test data
          if (currentSelectedSubflow) {
            updateSubflow(currentSelectedSubflow.id, {
              flowData: {
                ...currentFlowData,
                test: testFlowData,
              },
            });
          } else {
            updateSubflow("main", {
              flowData: {
                ...currentFlowData,
                test: testFlowData,
              },
            });
          }
        }

        // Build subflows data

        if (processedNodes.length === 0) {
          setSavingState(SavingState.ERROR);
          return;
        }

        // Create payload based on current flow mode
        // Get the latest values from refs
        const latestMainFlow = mainFlowRef.current;
        const latestSubflows = subflowsRef.current;
        const latestSelectedSubflow = selectedSubflowRef.current;

        // Determine which flow we're currently editing
        const isEditingMainFlow =
          !latestSelectedSubflow || latestSelectedSubflow.id === "main";
        const currentlyEditingSubflowId = latestSelectedSubflow?.id;

        // Build main flow data - use current canvas data if editing main flow
        let mainFlowLiveNodes,
          mainFlowLiveEdges,
          mainFlowTestNodes,
          mainFlowTestEdges;

        if (isEditingMainFlow) {
          // Use current canvas data for the flow we're editing
          if (flowMode === FlowMode.LIVE) {
            mainFlowLiveNodes = processedNodes;
            mainFlowLiveEdges = edges;
            mainFlowTestNodes = processNodes(
              latestMainFlow.flowData.test.nodes,
            );
            mainFlowTestEdges = latestMainFlow.flowData.test.edges;
          } else {
            mainFlowLiveNodes = processNodes(
              latestMainFlow.flowData.live.nodes,
            );
            mainFlowLiveEdges = latestMainFlow.flowData.live.edges;
            mainFlowTestNodes = processedNodes;
            mainFlowTestEdges = edges;
          }
        } else {
          // Not editing main flow, use context data
          mainFlowLiveNodes = processNodes(latestMainFlow.flowData.live.nodes);
          mainFlowLiveEdges = latestMainFlow.flowData.live.edges;
          mainFlowTestNodes = processNodes(latestMainFlow.flowData.test.nodes);
          mainFlowTestEdges = latestMainFlow.flowData.test.edges;
        }

        // Process all subflows - use current canvas data if editing that subflow
        const processedSubflows = latestSubflows.map(subflow => {
          const isEditingThisSubflow = subflow.id === currentlyEditingSubflowId;

          let liveNodes, liveEdges, testNodes, testEdges;

          if (isEditingThisSubflow) {
            // Use current canvas data for the subflow we're editing
            if (flowMode === FlowMode.LIVE) {
              liveNodes = processedNodes;
              liveEdges = edges;
              testNodes = processNodes(subflow.flowData.test.nodes);
              testEdges = subflow.flowData.test.edges;
            } else {
              liveNodes = processNodes(subflow.flowData.live.nodes);
              liveEdges = subflow.flowData.live.edges;
              testNodes = processedNodes;
              testEdges = edges;
            }
          } else {
            // Not editing this subflow, use context data
            liveNodes = processNodes(subflow.flowData.live.nodes);
            liveEdges = subflow.flowData.live.edges;
            testNodes = processNodes(subflow.flowData.test.nodes);
            testEdges = subflow.flowData.test.edges;
          }

          return {
            id: subflow.id,
            name: subflow.name,
            description: subflow.description,
            flowData: {
              live: {
                version: subflow.flowData.live.version,
                nodes: liveNodes,
                edges: liveEdges,
              },
              test: {
                version: subflow.flowData.test.version,
                nodes: testNodes,
                edges: testEdges,
              },
            },
          };
        });

        // Build the complete payload with all subflows
        const completeFlowData = {
          mainFlow: {
            live: {
              version: latestMainFlow.flowData.live.version,
              nodes: mainFlowLiveNodes,
              edges: mainFlowLiveEdges,
            },
            test: {
              version: latestMainFlow.flowData.test.version,
              nodes: mainFlowTestNodes,
              edges: mainFlowTestEdges,
            },
          },
          subflows: processedSubflows,
        };

        // Generate FSM from the same data that's being saved (test nodes)
        const mainFlowForFsm = {
          ...latestMainFlow,
          flowData: {
            ...latestMainFlow.flowData,
            test: {
              version: latestMainFlow.flowData.test.version,
              nodes: mainFlowTestNodes,
              edges: mainFlowTestEdges,
            },
          },
        };

        const subflowsForFsm = latestSubflows.map(subflow => {
          const processedSubflow = processedSubflows.find(
            ps => ps.id === subflow.id,
          );
          return {
            ...subflow,
            flowData: {
              ...subflow.flowData,
              test: {
                version:
                  processedSubflow?.flowData.test.version ||
                  subflow.flowData.test.version,
                nodes:
                  processedSubflow?.flowData.test.nodes ||
                  subflow.flowData.test.nodes,
                edges:
                  processedSubflow?.flowData.test.edges ||
                  subflow.flowData.test.edges,
              },
            },
          };
        });

        const fsmData = convertToFSM(
          mainFlowForFsm,
          subflowsForFsm,
          syncVariablesWithNodes,
        );

        // Use blueprint format nodes (not processed) for issue checking
        // Convert processed nodes back to blueprint format
        const mainFlowBlueprintNodes = restoreNodeSettings(
          mainFlowTestNodes as unknown as Node<FlowNodeData>[],
        );

        const subflowsToCheck = processedSubflows.map(subflow => ({
          id: subflow.id,
          name: subflow.name,
          flowData: {
            test: {
              nodes: restoreNodeSettings(
                subflow.flowData.test.nodes as unknown as Node<FlowNodeData>[],
              ),
              edges: subflow.flowData.test.edges,
              version: subflow.flowData.test.version,
            },
          },
        }));

        const mainFlowToCheck = {
          flowData: {
            test: {
              nodes: mainFlowBlueprintNodes,
              edges: mainFlowTestEdges,
              version: latestMainFlow.flowData.test.version,
            },
          },
        };

        const baseIssues = findAllSubflowsIssues(
          mainFlowToCheck,
          subflowsToCheck,
          variables,
        );

        if (baseIssues.length > 0) {
          setFlowIssues([...baseIssues]);
        } else {
          remoteValidateFsmCollectIssues({
            modelId: FlowBuilderSettings.modelId,
            fsmData: fsmData,
            nodes: processedNodes as unknown as Node<FlowNodeData>[],
          }).then(res => {
            setFlowIssues([...res]);
          });
        }

        const payload = {
          json_flow_designer: JSON.stringify(completeFlowData),
        } as Assistant & {fsm?: unknown};

        if (saveFSM) {
          payload.fsm = fsmData as unknown as Record<string, unknown>;
        }

        updateAssistantDetails(modelID, {
          ...payload,
        } as Assistant)
          .then(data => {
            setHasUnsavedChanges(false);
            setSavingState(SavingState.SAVED);
            if (afterSave) {
              afterSave();
            }
          })
          .catch(error => {
            console.error(error);
            setSavingState(SavingState.ERROR);
          })
          .finally(() => {
            // Update initial flow data with all flows after successful save
            const latestMainFlow = mainFlowRef.current;
            const latestSubflows = subflowsRef.current;
            const modeKey = flowMode === FlowMode.LIVE ? "live" : "test";

            const allFlowsData = JSON.stringify({
              mainFlow: serializeFlowData(
                latestMainFlow.flowData[modeKey].nodes,
                latestMainFlow.flowData[modeKey].edges,
              ),
              subflows: latestSubflows.map(sf =>
                serializeFlowData(
                  sf.flowData[modeKey].nodes,
                  sf.flowData[modeKey].edges,
                ),
              ),
            });
            setInitialFlowData(allFlowsData);
          });
      }, DEBOUNCE_TIME),
    [getNodes, getEdges, flowMode, syncVariablesWithNodes, updateSubflow],
  );

  const handleSave = useCallback(
    (
      afterSave?: () => void,
      saveFSM: boolean = false,
      allowOnLiveMode: boolean = false,
    ) => {
      // Check BOTH blockSave (temporary) AND permanentBlockSave (after load failure)
      if (blockSave || permanentBlockSave) {
        return;
      }

      if (
        (flowMode === FlowMode.TEST && !FlowBuilderSettings.demoMode) ||
        (flowMode === FlowMode.LIVE && allowOnLiveMode)
      ) {
        debouncedSave(afterSave, saveFSM);
      }
    },
    [flowMode, debouncedSave, blockSave, permanentBlockSave],
  );

  // Helper function to process a single key-value pair
  const processActionUpdate = useCallback((key: string, value: any) => {
    let data = {} as CustomAction | TransferCallAction | RealTimeBookingAction;

    switch (key) {
      case "endpoint":
        data = {
          url: value.url,
          http_method: value.http_method,
        } as CustomAction;
        break;
      case "headers":
        data = actionNodeTransform.convertActionHeaders(value) as CustomAction;
        break;
      case "authentication":
        data = actionNodeTransform.convertActionAuthentication(
          value,
        ) as CustomAction;
        break;
      case "body_and_variables":
        data = actionNodeTransform.convertActionBodyAndVariables(
          value,
        ) as CustomAction;
        break;
      case "start_message":
        data = {start_message: value} as CustomAction;
        break;
      case "delay_message":
        data = {delay_message: value} as CustomAction;
        break;
      case "failure_message":
        data = {failure_message: value} as CustomAction;
        break;
      case "speak_naturally":
        data = {speak_naturally: value} as CustomAction;
        break;
      case "name":
        data = {name: value} as CustomAction;
        break;
      case "description":
        data = {description: value} as CustomAction;
        break;
      case "variables":
        data = {variables: value} as CustomAction;
        break;
      case "api_response":
        data = {api_response: value} as CustomAction;
        break;
      default:
        data = {} as CustomAction;
    }

    return data;
  }, []);

  const handleSaveAction = useCallback(
    (
      actionId: string,
      paramOrUpdates: SettingParam | Record<string, any>,
      value?: any,
    ) => {
      // CRITICAL: Block action saves too if flow failed to load
      if (blockSave || permanentBlockSave) {
        return;
      }

      let finalData = {} as
        | CustomAction
        | TransferCallAction
        | RealTimeBookingAction;

      // Check if it's the new format (object with multiple updates)
      if (typeof paramOrUpdates === "object" && !("key" in paramOrUpdates)) {
        // New format: handleSaveAction(actionId, {key1: value1, key2: value2})
        const updates = paramOrUpdates as Record<string, any>;

        Object.entries(updates).forEach(([key, val]) => {
          const updateData = processActionUpdate(key, val);
          finalData = {...finalData, ...updateData};
        });
      } else {
        // Original format: handleSaveAction(actionId, param, value)
        const param = paramOrUpdates as SettingParam;
        finalData = processActionUpdate(param.key, value);
      }

      try {
        setSavingState(SavingState.SAVING);
        updateAction({
          slug: actionId,
          data: {...finalData},
        });
      } catch (error) {
        setSavingState(SavingState.ERROR);
        console.error(error);
      } finally {
        setSavingState(SavingState.SAVED);
      }
    },
    [processActionUpdate, updateAction, blockSave, permanentBlockSave],
  );

  // Get current flow data from selected subflow
  const getCurrentFlowData = () => {
    return selectedSubflow?.flowData || mainFlow.flowData;
  };

  return (
    <SaveContext.Provider
      value={{
        savingState,
        handleSave,
        setSavingState,
        hasUnsavedChanges,
        setHasUnsavedChanges,
        setInitialFlowData,
        initialFlowData,
        flowIssues,
        setFlowIssues,
        flowMode,
        setFlowMode,
        flowData: getCurrentFlowData(),
        setFlowData: newFlowData => {
          // Update the selected subflow with the new flow data
          if (selectedSubflow) {
            updateSubflow(selectedSubflow.id, {flowData: newFlowData});
          }
        },
        handleSaveAction,
        blockSave,
        setBlockSave,
        permanentBlockSave,
        setPermanentBlockSave,
        processActionUpdate,
      }}
    >
      {children}
    </SaveContext.Provider>
  );
};

export const useSave = () => {
  const context = useContext(SaveContext);
  if (!context) {
    throw new Error("useSave must be used within a SaveProvider");
  }
  return context;
};
